import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useInViewport } from '../calendar/app/hooks';
import { SvgIcon } from '../calendar/components/svg-icon';
import { DATE_FORMAT, insertEventsIntoCalendar, sortEventsChronologically } from '../calendar/helpers/date-helpers';
import { replaceWithTags } from '../calendar/helpers/react-text-helpers';
import { EventList } from './components/event-list';
import { MiniCalendar } from './components/mini-calendar';
import { PromoCode } from './components/promo-code';
function App() {
    const pdpEvents = window.pdpEvents;
    const specialFlagLegend = window.specialFlagLegend;
    const paginationLimit = window.paginationLimit || 10;
    const startWithList = window.startWithList;
    const sidebarHeading = window.sidebarHeading;
    const sidebarRange = window.sidebarRange;
    const relatedInfoLabelText = window.relatedInfoLabelText || "Related Event Details";
    const showingPerformancesText = window.showingPerformancesText || "Showing all available performances.";
    const showingSomePerformancesText = window.showingSomePerformancesText || "Showing {0} of {1} performances.";
    const showingPerformancesForDateText = window.showingPerformancesForDateText || "Showing performances for ";
    const viewAsText = window.viewAsText || "View as ";
    const showAllMonthPerformancesText = window.showAllMonthPerformancesText || "Show all {0} performances";
    const nextAvailableDateText = window.nextAvailableDateText || "Next available date";
    const showPromoCode = window.showPromoCode || false;
    const emptyMonthMessaging = window.emptyMonthMessaging || "<p>No events this month.</p>";
    const nextMonthCtaText = window.nextMonthCtaText || "View {0} ({1} event(s))";
    const prevMonthCtaText = window.prevMonthCtaText || "View {0} ({1} event(s))";
    const todaysDate = dayjs().startOf('day');
    const sortedEvents = sortEventsChronologically(pdpEvents);
    const startDate = sortedEvents[0]?.eventDate;
    const endDate = sortedEvents[sortedEvents.length - 1]?.eventDate;
    const dateRange = { startDate: dayjs(startDate).startOf('month').format(DATE_FORMAT), endDate: dayjs(endDate).endOf('month').format(DATE_FORMAT) };
    const calendarOfEvents = insertEventsIntoCalendar(pdpEvents, dateRange, true);
    const listHeading = useRef(null);
    const firstLoad = useRef(true);
    const appContainer = useRef(null);
    const [layoutType, setLayoutType] = useState(startWithList ? 'list' : 'calendar');
    const [currentMonth, setMonth] = useState(0);
    const [selectedDate, setSelectedDate] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const appIsInViewport = useInViewport(appContainer);
    const flatEventList = calendarOfEvents?.flatMap(month => {
        return month.days.flatMap(day => day.events);
    });
    const daysWithEvents = calendarOfEvents?.flatMap(month => {
        return month.days.filter(day => {
            return day.events.length > 0;
        });
    });
    const getCurrentEventList = () => {
        if (selectedDate) {
            return calendarOfEvents[currentMonth]?.days.filter(day => day.date == selectedDate).flatMap(day => day.events);
        }
        else {
            if (layoutType == 'calendar') {
                return calendarOfEvents[currentMonth]?.days.flatMap(day => day.events);
            }
            else {
                return flatEventList;
            }
        }
    };
    const allEvents = getCurrentEventList();
    let currentEvents = allEvents.filter(event => todaysDate.isBefore(event.eventDate));
    if (currentEvents.length == 0) {
        currentEvents = allEvents;
    }
    const totalMonths = calendarOfEvents.map(month => month.month);
    const monthSelected = (newMonth) => {
        setSelectedDate(null);
        setMonth(newMonth);
    };
    const nextAvailableDate = () => {
        const currentDateIndex = daysWithEvents.findIndex(day => dayjs(day.date).format(DATE_FORMAT) == selectedDate);
        if (currentDateIndex > -1 && currentDateIndex != daysWithEvents.length - 1) {
            let nextDayWithAvailableEvents = null;
            for (let i = currentDateIndex + 1; i < daysWithEvents.length; i++) {
                if (daysWithEvents[i].events.some(event => event.isAvailable && event.onSale)) {
                    nextDayWithAvailableEvents = dayjs(daysWithEvents[i].date).format(DATE_FORMAT);
                    break;
                }
            }
            return nextDayWithAvailableEvents;
        }
        else {
            return null;
        }
    };
    const goToNextAvailableDate = () => {
        const nextDate = nextAvailableDate();
        if (nextDate) {
            if (!dayjs(nextDate).isSame(totalMonths[currentMonth], 'month')) {
                setMonth(currentMonth + 1);
            }
            setSelectedDate(nextDate);
        }
    };
    const toggleLayout = () => {
        if (layoutType == 'list') {
            setLayoutType('calendar');
        }
        else {
            setLayoutType('list');
        }
    };
    const scrollToTopOfList = () => {
        if (listHeading.current) {
            listHeading.current.scrollIntoView({ block: "start" });
            listHeading.current.focus({ preventScroll: true });
        }
    };
    const navigateToPage = (page) => {
        setCurrentPage(page);
        scrollToTopOfList();
    };
    useEffect(() => {
        if (currentPage != 1) {
            setCurrentPage(1);
        }
        if (firstLoad.current == true) {
            firstLoad.current = false;
        }
        else {
            scrollToTopOfList();
        }
    }, [selectedDate, currentMonth]);
    useEffect(() => {
        setSelectedDate(null);
        setMonth(0);
        setCurrentPage(1);
    }, [layoutType]);
    useEffect(() => {
        if (appIsInViewport) {
            document.documentElement.setAttribute('data-calendar-on-screen', 'true');
        }
        else {
            document.documentElement.setAttribute('data-calendar-on-screen', 'false');
        }
    }, [appIsInViewport]);
    const quantityMessage = () => {
        let somePerformancesArray = replaceWithTags(showingSomePerformancesText, "{0}", <span className="bold">{currentPage * paginationLimit - paginationLimit + 1} &ndash; {Math.min(currentPage * paginationLimit, currentEvents.length)}</span>, "{1}", <span className="bold">{currentEvents.length} performances</span>);
        return (<h3 className="pdp-event-list-qty" id="event-list-heading" ref={listHeading} tabIndex={-1}>
                {!!selectedDate ?
                <span>{showingPerformancesForDateText} <span className="bold">{dayjs(selectedDate).format('MMMM D, YYYY')}</span></span>
                :
                    paginationLimit >= currentEvents?.length ?
                        <span>{showingPerformancesText}</span>
                        :
                            <span>{somePerformancesArray.map(piece => (piece))}</span>}
            </h3>);
    };
    const layoutSwitcher = (mobile) => {
        return (<button type="button" className={`btn-custom btn-with-icon ${mobile ? 'pdp-cal-mobile-show' : 'pdp-cal-mobile-hide'}`} onClick={toggleLayout}>
                {viewAsText} {layoutType == 'list' ?
                <>Calendar <SvgIcon iconId={"icon-calendar"}/></> :
                <>List <SvgIcon iconId={"icon-list"}/></>}
            </button>);
    };
    const navigateToMonth = (next) => {
        const targetMonth = next ? currentMonth + 1 : currentMonth - 1;
        const targetMonthCount = calendarOfEvents[targetMonth].eventCount;
        const buttonText = next ? nextMonthCtaText : prevMonthCtaText;
        const formattedButton = `${buttonText.replace('{0}', dayjs(calendarOfEvents[targetMonth].month).format('MMMM')).replace('{1}', `${targetMonthCount}`).replace("(s)", (targetMonthCount == 1 ? "" : "s"))}`;
        return (<button type="button" className="btn-custom btn-with-icon" onClick={() => monthSelected(next ? currentMonth + 1 : currentMonth - 1)}>
                {!next && <SvgIcon iconId={"arrow-left"}/>}
                {formattedButton}
                {next && <SvgIcon iconId={"arrow-right"}/>}
            </button>);
    };
    return (<>
            {pdpEvents &&
            <div className={`ace-layout pdp-cal-layout ${layoutType == 'list' ? 'pdp-cal-layout-list' : 'pdp-cal-layout-calendar'}`} ref={appContainer}>
                    <div className="mini-cal-column">
                        <div className="mini-cal-content pdp-cal-content stack stack-border">
                            <div className="rich-text">
                                <h2 dangerouslySetInnerHTML={{ __html: sidebarHeading }}/>
                                <p className="large bold" dangerouslySetInnerHTML={{ __html: sidebarRange }}/>
                                {layoutSwitcher(true)}
                            </div>
                            {layoutType == 'calendar' &&
                    <div>
                                    <MiniCalendar eventData={calendarOfEvents[currentMonth]} totalMonths={totalMonths} setMonth={monthSelected} currentMonthIndex={currentMonth} clickedDate={setSelectedDate} selectedDate={selectedDate} todaysDate={todaysDate} specialFlagLegend={specialFlagLegend}/>
                                </div>}
                            {showPromoCode &&
                    <PromoCode />}
                        </div>
                    </div>
                    <div className="stack">
                        {<div className="pdp-event-messaging">
                                {layoutSwitcher()}
                                {quantityMessage()}
                            </div>}
                        <EventList events={currentEvents} paginationLimit={paginationLimit} selectedDate={selectedDate} resetDate={() => setSelectedDate(null)} currentPage={currentPage} setCurrentPage={navigateToPage} relatedInfoLabel={relatedInfoLabelText} nextAvailableDate={nextAvailableDate()} goToNextAvailableDate={goToNextAvailableDate} showAllMonthPerformancesText={showAllMonthPerformancesText} nextAvailableDateText={nextAvailableDateText} todaysDate={todaysDate} emptyMonthMessaging={emptyMonthMessaging} activeMonth={dayjs(calendarOfEvents[currentMonth].month)} nextMonthCta={layoutType == "calendar" && currentMonth < totalMonths.length - 1 && navigateToMonth(true)} previousMonthCta={layoutType == "calendar" && currentMonth > 0 && navigateToMonth(false)}/>
                    </div>
                </div>}
        </>);
}
export default App;
